import { ArrayUtil, LeftOrRightAlignment } from "@mcleod/core";
import { AbstractGeneratedColumnHandler } from "./AbstractGeneratedColumnHandler";
import { ChangeEvent } from "../../events/ChangeEvent";
import { Checkbox } from "../checkbox/Checkbox";
import { SelectCheckboxHeadingTableCell } from "./SelectCheckboxHeadingTableCell";
import { SelectCheckboxTableCell } from "./SelectCheckboxTableCell";
import { SelectCheckboxTableColumn } from "./SelectCheckboxTableColumn";
import { SelectionType } from "../../base/SelectionMode";
import { TableRow } from "./TableRow";
import { TableRowDisplayEvent } from "../../events/TableRowDisplayEvent";
import { TableSelectionEvent } from "../../events/TableSelectionEvent";
import { TableCell } from "./TableCell";

export class SelectCheckboxHandler extends AbstractGeneratedColumnHandler {
    protected override createColumn(): SelectCheckboxTableColumn {
        return new SelectCheckboxTableColumn({ table: this.table });
    }

    private get columnHeadingCell(): SelectCheckboxHeadingTableCell {
        return this.column.headingCell as SelectCheckboxHeadingTableCell
    }

    protected override get columnAlignment(): LeftOrRightAlignment {
        return this.table.selectCheckboxAlignment;
    }

    protected override getLogPath(): string {
        return "components.table.SelectCheckboxHandler";
    }

    protected override createPlaceholderCell(tableRow: TableRow): TableCell {
        return new SelectCheckboxTableCell({ table: this.table, row: tableRow });
    }

    private get selectAllCheckbox(): Checkbox {
        return this.columnHeadingCell.checkbox;
    }

    protected override addTableListeners() {
        this.table.addSelectionListener((event: TableSelectionEvent) => {
            this.syncAllCheckboxes(event);
        });
        this.table.addRowDisplayListener((event: TableRowDisplayEvent) => {
            const tableRow = event.getTableRow();
            const checkbox = this.getCheckboxInRow(tableRow);
            if (checkbox != null)
                checkbox.checked = this.table.selectedRows.includes(tableRow);
            this.syncSelectAllCheckbox();
        });
        this.table.addDataDisplayListener(this.syncSelectAllCheckbox);
        this.table.addContentsChangedListener(this.syncSelectAllCheckbox);
        this.table.addFilterChangedListener(this.syncSelectAllCheckbox);
    }

    syncAllCheckboxes(event?: TableSelectionEvent) {
        const selectedRows = this.table.selectedRows;
        this.table.rows.forEach(tableRow => {
            const checkbox = this.getCheckboxInRow(tableRow);
            if (checkbox != null) {
                if (selectedRows.includes(tableRow) && this.isRowDisabled(tableRow)) {
                    checkbox.checked = false;
                    this.table.selectRow(tableRow, SelectionType.DISTINCT, event.domEvent);
                } else
                    checkbox.checked = selectedRows.includes(tableRow);
            }
        });
        this.syncSelectAllCheckbox();
    }

    checkboxSelectAllOnChange = (event: ChangeEvent) => {
        this.log.debug(this, "Select All Checkbox changed, event=%o", event);
        if (event.userInitiatedChange !== true || this.table._designer != null) {
            this.log.debug(this, "Disregarding, either in designer or not a user-initiated change");
            return;
        }
        if (this.table.rows.every(tableRow => tableRow.selected || this.isRowDisabled(tableRow))) {
            this.log.debug(this, "Unselecting all rows");
            this.table.selectedRows = [];
        }
        else {
            this.table.selectedRows = this.table.rows.filter(tableRow => !this.isRowDisabled(tableRow));
            this.log.debug(this, "Selected all enabled rows: %o", this.table.selectedRows);
        }
        this.syncSelectAllCheckbox();
    }

    private syncSelectAllCheckbox = () => {
        if (this.table.rows.length === 0) {
            this.log.debug(this, "Unchecking and disabling Select All Checkbox, table is empty");
            this.selectAllCheckbox.checked = false;
            this.selectAllCheckbox.enabled = false;
            return;
        }
        this.log.debug(this, "Enabling Select All Checkbox");
        this.selectAllCheckbox.enabled = true;

        // Only set allowIndeterminate here, when we know we might need it.  This allows normal toggling of the Select
        // All Checkbox when the user interacts with it.  The user should never be able to put the checkbox into the
        // indeterminate/undefined state...that should only be possible based on the user changing individual
        // row selections.
        this.selectAllCheckbox.allowIndeterminate = false;
        if (this.table.rows.every(tableRow => tableRow.selected || this.isRowDisabled(tableRow))) {
            this.log.debug(this, "Checking Select All Checkbox");
            this.selectAllCheckbox.checked = true;
        } else if (this.table.rows.every(tableRow => !tableRow.selected)) {
            this.log.debug(this, "Unchecking Select All Checkbox");
            this.selectAllCheckbox.checked = false;
        } else {
            this.log.debug(this, "Setting Select All Checkbox to indeterminate, only some rows are selected");
            this.selectAllCheckbox.allowIndeterminate = true;
            this.selectAllCheckbox.checked = undefined;
        }
    }

    public getCheckboxInRow(tableRow: TableRow): Checkbox {
        const cells = tableRow.cells?.filter(cell => {
            return cell instanceof SelectCheckboxTableCell || cell instanceof SelectCheckboxHeadingTableCell;
        });
        return ArrayUtil.getFirstElement(cells)?.checkbox;
    }

    private isRowDisabled(tableRow: TableRow): boolean {
        const checkbox = this.getCheckboxInRow(tableRow);
        const result = tableRow?.enabled === false || checkbox?.enabled === false;
        this.log.debug(this, "Testing if TableRow is disabled.  Row=%o, Is Disabled=%o", tableRow, result);
        return result;
    }
}
